//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { CircleDash } from '@carbon/icons-react';

const RotatingFeatureBox: React.FC = () => {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const features = [
    {
      title: t('feature-ci-cd-title'),
      description: t('feature-ci-cd-description'),
    },
    {
      title: t('feature-vsm-title'),
      description: t('feature-vsm-description'),
    },
    {
      title: t('feature-collaborative-title'),
      description: t('feature-collaborative-description'),
    },
  ];

  useEffect(() => {
    let transitionTimeout: ReturnType<typeof setTimeout>;
    const timer = setInterval(() => {
      setIsTransitioning(true);
      transitionTimeout = setTimeout(() => {
        setActive((prev) => (prev + 1) % features.length);
        setIsTransitioning(false);
      }, 300);
    }, 15000);
    return () => {
      clearInterval(timer);
      clearTimeout(transitionTimeout);
    };
  }, []);

  const feature = features[active];

  return (
    <div
      id="id-rotating-feature-container"
      data-testid="rotating-feature-container"
      className="relative overflow-hidden rounded-lg border border-carbon-border bg-carbon-light p-6 dark:border-carbon-darkBorderSurface dark:bg-carbon-darkSurface"
    >
      <div
        id="id-rotating-feature-icon-wrapper"
        data-testid="rotating-feature-icon-wrapper"
        className="absolute right-4 top-4"
      >
        <CircleDash size={24} className="animate-spin text-brand-blue" />
      </div>
      <div
        id="id-rotating-feature-content"
        data-testid="rotating-feature-content"
        className={`transition-opacity duration-300 ${isTransitioning ? 'opacity-0' : 'opacity-100'}`}
      >
        <h3 className="mb-3 pr-10 text-lg font-medium text-carbon-dark dark:text-white">
          {feature.title}
        </h3>
        <p className="text-sm leading-relaxed text-carbon-textSecondary dark:text-carbon-textMuted">
          {feature.description}
        </p>
      </div>
      <div
        id="id-rotating-feature-indicators"
        data-testid="rotating-feature-indicators"
        className="mt-6 flex gap-2"
      >
        {features.map((_, i) => (
          <div
            key={i}
            id={`id-rotating-feature-indicator-${i}`}
            data-testid={`rotating-feature-indicator-${i}`}
            className={`h-1 flex-1 rounded-full transition-colors duration-300 ${
              i === active ? 'bg-brand-blue' : 'bg-carbon-border'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

export default RotatingFeatureBox;
